import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { MergedRoute } from '@next/core-lib/router-store';
import { SettingsStorage } from '@next/core-lib/settings';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RouterNavigationAction, ROUTER_NAVIGATION } from '@ngrx/router-store';
import { Action, select, Store } from '@ngrx/store';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { FlockRegistrationService } from 'src/app/cycle/services/flock-registration.service';
import {
  FeedIntakeUnit,
  WaterIntakeUnit,
  FeedUnit,
  TemperatureUnit,
  WaterUnit,
  WeightUnit,
} from 'src/app/reference-value-sheet/models/reference-value-sheet.model';
import { SettingsServiceHelper } from 'src/app/shared/services/settingsHelperService';

import { UpsertBarnResponse } from '../../models/barn-service.model';
import { SensorActuatorBarnMapping } from '../../models/farm-result.model';
import {
  Barn,
  RVSSettings,
  SettingModuleState,
  UnitOfMeasureSettings,
  VeterinarianSettings,
  FinalSetupSettings,
  DiscountSettings,
} from '../../models/setting.model';
import { BarnService } from '../../services/barn.service';
import { FarmResultService } from '../../services/farm-result.service';
import * as settingActions from '../actions/setting.actions';
import { getSelectedBarn, getSelectedBarnId } from '../selectors/setting.selector';
import { PoultryContextKeys } from './../../../shared/constants';

@Injectable()
export class SettingEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<SettingModuleState>>(Store);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private barnService = inject(BarnService);
  private farmResultService = inject(FarmResultService);
  private dialogService = inject(DialogService);
  private readonly locale = inject<LocaleService>(LocaleService);
  private settingsHelper = inject(SettingsServiceHelper);
  private flockRegistrationService = inject(FlockRegistrationService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  loadBarns$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadBarnsRequest),
      mergeMap(() =>
        this.barnService.getBarns().pipe(
          withLatestFrom(this.store.select(getSelectedBarnId)),
          switchMap(([barns, barnId]: [Barn[], number]) =>
            of(
              settingActions.loadBarnsSuccess({ barns }),
              settingActions.setSelectedBarn({ barnId }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.loadBarnsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteBarnRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.deleteBarnRequest),
      mergeMap(({ barn }) =>
        this.barnService.deleteBarn(barn.id).pipe(
          map(() => settingActions.deleteBarnSuccess({ barn })),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.deleteBarnFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  saveSelectedBarn$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertBarn),
      switchMap((_) => this.store.pipe(select(getSelectedBarn), take(1))),
      map((barn: Barn) => settingActions.upsertBarnRequest({ barn })),
    ),
  );

  upsertBarnRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertBarnRequest),
      exhaustMap(({ barn }) =>
        this.barnService.upsertBarn(barn).pipe(
          map((response: UpsertBarnResponse) =>
            settingActions.upsertBarnSuccess({
              barn: {
                ...barn,
                id: response.id,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.upsertBarnFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  changeNavigationUrl$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      map(
        (action: RouterNavigationAction<MergedRoute>) =>
          action.payload.routerState.params['barnId'],
      ),
      map((barnId) => settingActions.setSelectedBarn({ barnId: parseInt(barnId, 10) })),
    ),
  );

  upsertBarnRequestSucces$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(settingActions.upsertBarnSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/settings', 'barn'])).then();
        }),
      ),
    { dispatch: false },
  );

  deleteBarnWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.deleteBarnWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfimation('barn-item')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                settingActions.deleteBarnRequest({
                  barn: action.barn,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  loadDayOffset$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadDayOffset),
      switchMap((_) =>
        this.settingsHelper
          .getSingle(0, PoultryContextKeys.DAYOFFSET, SettingsStorage.Global, false, true, false)
          .pipe(
            map((v: number) => settingActions.setDayOffset({ dayOffset: v, save: false })),
            catchError(() => of(settingActions.setDayOffset({ dayOffset: 0, save: false }))),
          ),
      ),
    ),
  );

  setDayOffset$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(settingActions.setDayOffset),
        tap((action) => {
          if (action.save === true) {
            this.settingsHelper.setSettings(
              PoultryContextKeys.DAYOFFSET,
              action.dayOffset,
              SettingsStorage.Global,
              false,
              true,
              false,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  loadObservationSwitch$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadObservationSwitch),
      switchMap((_) =>
        this.settingsHelper
          .getSingle(
            true,
            PoultryContextKeys.OBSERVATIONS_SWITCH,
            SettingsStorage.Global,
            false,
            true,
            false,
          )
          .pipe(
            map((v: boolean) =>
              settingActions.setObservationSwitch({ observationSwitch: v, save: false }),
            ),
            catchError(() => [
              CoreActions.fatalError({
                code: 500,
                message: this.locale.translate('general-settings-error'),
              }),
            ]),
          ),
      ),
    ),
  );

  setObservationSwitch$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(settingActions.setObservationSwitch),
        tap((action) => {
          if (action.save === true) {
            this.settingsHelper.setSettings(
              PoultryContextKeys.OBSERVATIONS_SWITCH,
              action.observationSwitch,
              SettingsStorage.Global,
              false,
              true,
              false,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  loadRVSSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadRVSSettings),
      switchMap((_) =>
        this.settingsHelper
          .getSingle(
            {
              temperatureUnit: TemperatureUnit.Celcius,
              feedUnit: FeedUnit.Gram,
              waterUnit: WaterUnit.ML,
              weightUnit: WeightUnit.Gram,
            } as RVSSettings,
            PoultryContextKeys.RVS_SETTINGS,
            SettingsStorage.Global,
            false,
            true,
            false,
          )
          .pipe(
            map((value: RVSSettings) =>
              settingActions.setRVSSettings({
                value,
                save: false,
              }),
            ),
            catchError(() => [
              CoreActions.fatalError({
                code: 500,
                message: this.locale.translate('general-settings-error'),
              }),
            ]),
          ),
      ),
    ),
  );

  setRVSSettings$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(settingActions.setRVSSettings),
        tap((action) => {
          if (action.save === true) {
            this.settingsHelper.setSettings(
              PoultryContextKeys.RVS_SETTINGS,
              {
                temperatureUnit: action.value.temperatureUnit,
                feedUnit: action.value.feedUnit,
                waterUnit: action.value.waterUnit,
                weightUnit: action.value.weightUnit,
              },
              SettingsStorage.Global,
              false,
              true,
              false,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  loadUnitOfMeasureSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadUnitOfMeasureSettings),
      switchMap((_) =>
        this.settingsHelper
          .getSingle(
            {
              feedIntakeUnit: FeedIntakeUnit.CumulativeKilogram,
              waterIntakeUnit: WaterIntakeUnit.CumulativeLiter,
            } as UnitOfMeasureSettings,
            PoultryContextKeys.UNIT_OF_MEASURE_SETTINGS,
            SettingsStorage.Global,
            false,
            true,
            false,
          )
          .pipe(
            map((value: UnitOfMeasureSettings) =>
              settingActions.setUnitOfMeasureSettings({
                value,
                save: false,
              }),
            ),
            catchError(() => [
              CoreActions.fatalError({
                code: 500,
                message: this.locale.translate('general-settings-error'),
              }),
            ]),
          ),
      ),
    ),
  );

  setUnitOfMeasureSettings$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(settingActions.setUnitOfMeasureSettings),
        tap((action) => {
          if (action.save === true) {
            this.settingsHelper.setSettings(
              PoultryContextKeys.UNIT_OF_MEASURE_SETTINGS,
              {
                feedIntakeUnit: action.value.feedIntakeUnit,
                waterIntakeUnit: action.value.waterIntakeUnit,
              },
              SettingsStorage.Global,
              false,
              true,
              false,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  loadFarmResultSettingsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadFarmResultSettingsRequest),
      mergeMap(() =>
        this.farmResultService.getSensorActuatorBarns().pipe(
          switchMap((sensorActuatorBarns: SensorActuatorBarnMapping[]) =>
            of(settingActions.loadFarmResultSettingsSuccess({ sensorActuatorBarns })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.loadBarnsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertFarmResultSettingsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertFarmResultSettingsRequest),
      debounceTime(100),
      mergeMap(({ sensorActuatorBarns }) =>
        forkJoin(
          sensorActuatorBarns.map((sab) => this.farmResultService.upsertSensorActuatorBarn(sab)),
        ).pipe(
          map((_sensorActuatorBarnResponses) =>
            settingActions.upsertFarmResultSettingsSuccess({ sensorActuatorBarns }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.upsertFarmResultSettingsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertFarmResultSettingsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(settingActions.upsertFarmResultSettingsSuccess),
        tap((_) => {
          this.ngZone.run(() => this.router.navigate(['/settings', 'farmresultsetting'])).then();
        }),
      ),
    {
      dispatch: false,
    },
  );

  upsertVeterinarianSettings$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(settingActions.upsertVeterinarianSettings),
        tap((action) => {
          this.settingsHelper.setSettings(
            PoultryContextKeys.VETERINARIAN_SETTINGS,
            {
              ...action.veterinarianSettings,
            },
            SettingsStorage.Global,
            false,
            true,
            false,
          );
        }),
      ),
    { dispatch: false },
  );

  loadVeterinarianSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadVeterinarianSettings),
      switchMap((_) =>
        this.settingsHelper
          .getSingle(
            {
              id: '',
              name: '',
            } as VeterinarianSettings,
            PoultryContextKeys.VETERINARIAN_SETTINGS,
            SettingsStorage.Global,
            false,
            true,
            false,
          )
          .pipe(
            map((value: VeterinarianSettings) =>
              settingActions.upsertVeterinarianSettings({
                veterinarianSettings: value,
              }),
            ),
            catchError(() => [
              CoreActions.fatalError({
                code: 500,
                message: this.locale.translate('general-settings-error'),
              }),
            ]),
          ),
      ),
    ),
  );

  loadFinalSetupSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadFinalSetupSettings),
      switchMap((_) =>
        this.settingsHelper
          .getSingle<FinalSetupSettings>(
            {
              finalSetup: false,
              chickDeliveries: false,
              feedDeliveries: false,
              departures: false,
              discounts: false,
              masterData: {
                notes: '',
                payer: '',
                payerEmail: '',
                representativeAgrifirm: '',
                separateStable: false,
                integrationPremium: false,
                discountValue: null,
                agrifirmMember: false,
              },
            },
            PoultryContextKeys.FINALSETUP_SETTINGS,
            SettingsStorage.Global,
            false,
            false,
            true,
          )
          .pipe(
            map((value: FinalSetupSettings) =>
              settingActions.setFinalSetupSettings({
                value,
                save: false,
              }),
            ),
            catchError(() => [
              CoreActions.fatalError({
                code: 500,
                message: this.locale.translate('general-settings-error'),
              }),
            ]),
          ),
      ),
    ),
  );

  setFinalSetupSettings$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(settingActions.setFinalSetupSettings),
        debounceTime(400),
        tap((action) => {
          if (action.save === true) {
            this.settingsHelper.setSettings(
              PoultryContextKeys.FINALSETUP_SETTINGS,
              {
                finalSetup: action.value.finalSetup,
                chickDeliveries: action.value.chickDeliveries,
                feedDeliveries: action.value.feedDeliveries,
                departures: action.value.departures,
                discounts: action.value.discounts,
                masterData: {
                  notes: action.value.masterData?.notes,
                  payer: action.value.masterData?.payer,
                  payerEmail: action.value.masterData?.payerEmail,
                  representativeAgrifirm: action.value.masterData?.representativeAgrifirm,
                  separateStable: action.value.masterData?.separateStable,
                  integrationPremium: action.value.masterData?.integrationPremium,
                  discountValue: action.value.masterData?.discountValue,
                  agrifirmMember: action.value.masterData?.agrifirmMember,
                },
              },
              SettingsStorage.Global,
              false,
              false,
              true,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  upsertMasterDataRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertMasterDataRequest),
      debounceTime(400),
      exhaustMap(({ masterData }) =>
        this.flockRegistrationService.upsertMasterData(masterData).pipe(
          switchMap(() => EMPTY),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadDiscountSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadDiscountSettings),
      switchMap(() =>
        this.settingsHelper
          .getSingle<DiscountSettings>(
            {
              memberDiscountNL: null,
              memberBenefitNL: null,
              memberBenefitAbroad: null,
            },
            PoultryContextKeys.DISCOUNT_SETTINGS,
            SettingsStorage.Global,
            false,
            false,
            false,
          )
          .pipe(
            map((value: DiscountSettings) =>
              settingActions.setDiscountSettigs({
                discountSettings: value,
              }),
            ),
            catchError(() => [
              CoreActions.fatalError({
                code: 500,
                message: this.locale.translate('general-settings-error'),
              }),
            ]),
          ),
      ),
    ),
  );

  upsertDiscountSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertDiscountSettings),
      switchMap((action) =>
        this.settingsHelper
          .setSettingsAndGetId(
            PoultryContextKeys.DISCOUNT_SETTINGS,
            action.discountSettings,
            SettingsStorage.Global,
            false,
            false,
            false,
          )
          .pipe(
            map(() =>
              settingActions.setDiscountSettigs({ discountSettings: action.discountSettings }),
            ),
            catchError((error: HttpErrorResponse) =>
              of(NotificationActions.errorSnackbar({ message: error.message })),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  private getDeleteDialogConfimation(type: string) {
    return {
      text: this.locale.translate(`delete-item-${type}`),
    };
  }
}
